import { useEffect, useState } from 'react';
import StoreType from '@/shared/enums/StoreType';
import { useDesignsContext } from '@/shared/providers/DesignsProvider';
import mapDesign from '@/shared/utils/mapDesign';
import useAccess from '@pkg/access/designs/useAccess';
import * as Library from '@pkg/entities/library';
import makeGraph from '@pkg/entities/snapshots/graph/makeGraph.js';
import { ActivityState } from '@/lib/enums';
import useDesignStore from '@/components/DesignContainer/hooks/useDesignStore';

/**
 * Returns a design from the store and falls back to a hydrated design.
 * @param {Boolean} useStored
 * @param {Boolean} useComparison
 *
 * @return {Object}
 */
const useStoredDesign = ({
  allowChanges,
  designId,
  level,
  storeType = StoreType.HYDRATED,
  includeNested = true,
  optimisticSnapshot,
}) => {
  const library = Library.useLibrary();
  const hydratedDesigns = useDesignsContext();

  const storedConditions = useDesignStore((state) => state.conditions);
  const settings = useDesignStore((state) => state.settings);
  const storedDesign = useDesignStore((state) => state.design);
  const storedSnapshot = useDesignStore((state) => state.snapshot);
  const storedRevision = useDesignStore((state) => state.revision);

  const [design, setDesign] = useState(null);
  const [snapshot, setSnapshot] = useState(null);
  const [snapshotEntityMap, setSnapshotEntityMap] = useState(null);
  const [metics, setMetrics] = useState(null);

  // Use the stored snapshot for scenarios that prioritise optimistic changes
  // over the need for all entities that exists in the design.
  const selectedSnapshot = optimisticSnapshot
    ? storedSnapshot
    : storedRevision?.snapshot;

  // For now we're only concerned with the latest revision and we're using
  // this instead of the stored access because this might be a comparison
  // or hyrdated design we need access permissions for.
  const access = useAccess(design, snapshot, false);
  const [hash, setHash] = useState(null);
  const [conditions, setConditions] = useState(null);
  const [previousNested, setNested] = useState(null);

  // This effect is for loading a hyrdated design.
  useEffect(() => {
    if (!hydratedDesigns?.designs?.size || storeType !== StoreType.HYDRATED) {
      return;
    }

    const { designs } = hydratedDesigns;

    if (!designs.has(designId)) {
      setDesign(null);
      setSnapshot(null);
      setSnapshotEntityMap(null);
      setHash(null);
      return;
    }

    const hydrated = designs.get(designId);

    // Early return if the design and snapshot in the store are the same as the
    // hydrated design to avoid unnecessary re-renders and computations.
    if (
      designId === design?.uuid &&
      hydrated.design?.latest?.uuid === design?.latest?.uuid
    ) {
      return;
    }
    const graph = makeGraph(hydrated.snapshot, {
      activities: ActivityState.ACTIVE,
      library,
    });
    hydrated.snapshot = graph;
    hydrated.snapshotEntityMap = mapDesign(hydrated.design, hydrated.snapshot);

    setDesign(hydrated.design);
    setSnapshot(hydrated.snapshot);
    setSnapshotEntityMap(hydrated.snapshotEntityMap);
    setHash(hydrated.snapshot.__hash);
  }, [hydratedDesigns?.designs, designId, storeType]);

  // This effect is for loading a stored design.
  useEffect(() => {
    if (
      storeType !== StoreType.DESIGN ||
      !designId ||
      !selectedSnapshot?.__hash ||
      (snapshot && !allowChanges)
    ) {
      return;
    }

    const storedCollaborators = JSON.stringify(storedDesign?.collaborators);
    const designCollaborators = JSON.stringify(design?.collaborators);
    const conditionsString = JSON.stringify(conditions);
    const storedConditionsString = JSON.stringify(storedConditions);

    if (
      selectedSnapshot?.__hash === hash &&
      (designId === design?.uuid || design?.uuid === storedDesign?.uuid) &&
      storedCollaborators === designCollaborators &&
      includeNested === previousNested &&
      storedConditionsString === conditionsString
    ) {
      return;
    }

    const updatedSnapshot = optimisticSnapshot
      ? selectedSnapshot
      : makeGraph(selectedSnapshot, {
          activities: settings?.activities?.state,
          conditions: storedConditions,
          library,
        });

    // If the stored design is the same as the design id we can use that
    // design but we must derive the entity map.
    setNested(includeNested);
    setDesign(storedDesign);
    setSnapshot(updatedSnapshot);
    setSnapshotEntityMap(
      mapDesign(storedDesign, updatedSnapshot, level, includeNested, true)
    );
    setHash(selectedSnapshot.__hash);
    setConditions(storedConditions);
  }, [
    allowChanges,
    storeType,
    designId,
    design?.uuid,
    includeNested,
    JSON.stringify(storedDesign?.collaborators),
    storedDesign?.uuid,
    selectedSnapshot?.__hash,
    JSON.stringify(storedConditions),
  ]);

  return {
    access,
    design,
    snapshot,
    snapshotEntityMap,
  };
};

export default useStoredDesign;
